import { useState, useEffect, useContext } from 'react';
import { useSetRecoilState } from 'recoil';
import { Plus, FolderPlus, Check } from 'lucide-react';
import { toast } from 'sonner';

import { useChatMessages, ChainlitContext, threadOperationsEvents, ThreadOperationsEvent } from '@chainlit/react-client';

import { operationModalState, IOperation } from '@chainlit/react-client';
import { operationsService } from '@/services/operationsService';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface AddToOperationButtonProps {
  variant?: 'default' | 'outline' | 'ghost';
  size?: 'default' | 'sm' | 'lg' | 'icon';
  className?: string;
}

export default function AddToOperationButton({ 
  variant = 'outline', 
  size = 'sm',
  className = ''
}: AddToOperationButtonProps) {
  const apiClient = useContext(ChainlitContext);
  const { threadId } = useChatMessages();
  const setModalState = useSetRecoilState(operationModalState);
  
  const [operations, setOperations] = useState<IOperation[]>([]);
  const [threadOperations, setThreadOperations] = useState<IOperation[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    loadAllOperations();
  }, []);

  useEffect(() => {
    if (threadId) {
      loadThreadOperations();
    }
  }, [threadId]);

  // Listen for thread operations changes from other components
  useEffect(() => {
    if (!threadId) return;

    const unsubscribe = threadOperationsEvents.subscribe('thread_operations_changed', (event: ThreadOperationsEvent) => {
      // Only respond to events for the current thread
      if (event.threadId === threadId) {
        loadThreadOperations();
      }
    });

    return unsubscribe;
  }, [threadId]);

  // Listen for operations list updates
  useEffect(() => {
    const unsubscribe = threadOperationsEvents.subscribe('operations_updated', () => {
      loadAllOperations();
    });

    return unsubscribe;
  }, []);

  const loadAllOperations = async () => {
    try {
      const ops = await operationsService.getAllOperations(apiClient);
      setOperations(ops);
    } catch (error) {
      console.error('Failed to load operations:', error);
    }
  };

  const loadThreadOperations = async () => {
    if (!threadId) return;
    
    try {
      const ops = await operationsService.getOperationsForThread(apiClient, threadId);
      setThreadOperations(ops);
    } catch (error) {
      console.error('Failed to load thread operations:', error);
    }
  };

  const handleAddToOperation = async (operationId: string) => {
    if (!threadId) {
      toast.error('No active thread to add');
      return;
    }

    setIsLoading(true);
    try {
      await operationsService.addThreadToOperation(apiClient, operationId, threadId);
      
      const operation = operations.find(op => op.id === operationId);
      toast.success(`Added to ${operation?.name || 'operation'}`);
      
      // Notify other components about the change
      threadOperationsEvents.notifyThreadAddedToOperation(threadId, operationId);
      
      // Refresh thread operations
      loadThreadOperations();
    } catch (error) {
      toast.error('Failed to add thread to operation');
      console.error('Add to operation error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveFromOperation = async (operationId: string) => {
    if (!threadId) return;

    setIsLoading(true);
    try {
      await operationsService.removeThreadFromOperation(apiClient, operationId, threadId);
      
      const operation = operations.find(op => op.id === operationId);
      toast.success(`Removed from ${operation?.name || 'operation'}`);
      
      // Notify other components about the change
      threadOperationsEvents.notifyThreadRemovedFromOperation(threadId, operationId);
      
      // Refresh thread operations
      loadThreadOperations();
    } catch (error) {
      toast.error('Failed to remove thread from operation');
      console.error('Remove from operation error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateNew = () => {
    setModalState({
      isOpen: true,
      mode: 'create'
    });
  };

  if (!threadId) {
    return null;
  }

  const availableOperations = operations.filter(op => 
    !threadOperations.some(threadOp => threadOp.id === op.id)
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant={variant} 
          size={size} 
          className={`${size === 'icon' ? 'rounded-full h-8 w-8' : ''} ${className}`}
          disabled={isLoading}
        >
          <FolderPlus className={size === 'icon' ? '!size-6' : 'mr-2 h-4 w-4'} />
          {size !== 'icon' && (threadOperations.length > 0 ? 'Manage Operations' : 'Add to Operation')}
        </Button>
      </DropdownMenuTrigger>
      
      <DropdownMenuContent align="end" className="w-64">
        {/* Current Operations */}
        {threadOperations.length > 0 && (
          <>
            <div className="px-2 py-1.5 text-sm font-medium text-muted-foreground">
              Current Operations
            </div>
            {threadOperations.map((operation) => (
              <DropdownMenuItem
                key={`current-${operation.id}`}
                onClick={() => handleRemoveFromOperation(operation.id)}
                className="flex items-center justify-between"
              >
                <div className="flex items-center space-x-2 min-w-0 flex-1">
                  <div
                    className="flex w-3 h-3 rounded-full flex-shrink-0"
                    style={{ backgroundColor: operation.color }}
                  />
                  <span className="truncate">{operation.name}</span>
                </div>
                <Check className="h-4 w-4 text-green-600" />
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
          </>
        )}

        {/* Available Operations */}
        {availableOperations.length > 0 && (
          <>
            <div className="px-2 py-1.5 text-sm font-medium text-muted-foreground">
              Add to Operation
            </div>
            {availableOperations.map((operation) => (
              <DropdownMenuItem
                key={`available-${operation.id}`}
                onClick={() => handleAddToOperation(operation.id)}
              >
                <div className="flex items-center space-x-2 min-w-0 flex-1">
                  <div
                    className="flex w-3 h-3 rounded-full flex-shrink-0"
                    style={{ backgroundColor: operation.color }}
                  />
                  <span className="truncate">{operation.name}</span>
                </div>
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
          </>
        )}

        {/* Create New Operation */}
        <DropdownMenuItem onClick={handleCreateNew}>
          <Plus className="mr-2 h-4 w-4" />
          Create New Operation
        </DropdownMenuItem>

        {operations.length === 0 && (
          <div className="px-2 py-3 text-sm text-muted-foreground text-center">
            No operations exist yet
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

